// ui.js - UI management and utilities

import store from "./store.js";
import { showNotification } from "./utils.js";
import { apiUrl } from "./api-settings.js";
import { setupUserProfileSettings } from "./user-profile.js";
// Get DOM elements
const menuToggle = document.getElementById("menuToggle");
const sidebar = document.getElementById("sidebar");
const appContainer = document.getElementById("appContainer");
const loginModal = document.getElementById("loginModal");
const limitReachedModal = document.getElementById("limitReachedModal");
const confirmModal = document.getElementById("confirmModal");
const logoutModal = document.getElementById("logoutModal");
const policyModal = document.getElementById("policyModal");
const confirmTitle = document.getElementById("confirmTitle");
const confirmMessage = document.getElementById("confirmMessage");
const closeLearnMore = document.getElementById("closeLearnMore");
const limitCloseBtn = document.getElementById("limitCloseBtn");
const learnMoreModal = document.getElementById("learnMoreModal");
const closePolicy = document.getElementById("closePolicy");
const privacyLink = document.getElementById("privacyLink");
const loginPrivacyLink = document.getElementById("loginPrivacyLink");

/**
 * Set up the UI elements and event listeners
 */
function setupUI() {
  // Mobile menu toggle
  if (menuToggle) {
    menuToggle.addEventListener("click", () => {
      sidebar.classList.toggle("open");
    });
  }

  // Privacy policy link
  if (privacyLink) {
    privacyLink.addEventListener("click", (e) => {
      e.preventDefault();
      if (policyModal) policyModal.style.display = "flex";
    });
  }

  // Login privacy policy link
  if (loginPrivacyLink) {
    loginPrivacyLink.addEventListener("click", (e) => {
      e.preventDefault();
      if (policyModal) policyModal.style.display = "flex";
    });
  }

  // Close policy button
  if (closePolicy) {
    closePolicy.addEventListener("click", () => {
      if (policyModal) policyModal.style.display = "none";
    });
  }

  // Learn more button
  if (closeLearnMore) {
    closeLearnMore.addEventListener("click", hideLearnMore);
  }

  // Limit reached close button
  if (limitCloseBtn) {
    limitCloseBtn.addEventListener("click", hideLimitReached);
  }
  
  // Set up user profile component in settings if settings exists
  initializeUserProfile();
}

/**
 * Initialize the user profile component when settings are opened
 */
function initializeUserProfile() {
  // Check if settings toggle exists
  const settingsToggle = document.getElementById("settingsToggle");
  if (settingsToggle) {
    // Add event listener to initialize user profile when settings are opened
    settingsToggle.addEventListener("click", () => {
      // Add a small delay to ensure the settings container is rendered
      setTimeout(() => {
        setupUserProfileSettings();
      }, 100);
    });
  }
}

/**
 * Show a confirmation dialog
 * @param {string} title - Title of the confirmation dialog
 * @param {string} message - Message to display
 * @param {function} callback - Callback function to call with the result
 */
function showConfirm(title, message, callback) {
  confirmTitle.textContent = title;
  confirmMessage.textContent = message;
  store.setConfirmCallback(callback);
  confirmModal.style.display = "flex";
}

/**
 * Show the learn more modal
 */
function showLearnMore() {
  loginModal.style.display = "none";
  learnMoreModal.style.display = "flex";
}

/**
 * Hide the learn more modal
 */
function hideLearnMore() {
  learnMoreModal.style.display = "none";
  
  const isAuthenticated = store.getIsAuthenticated();
  if (!isAuthenticated) {
    loginModal.style.display = "flex";
  }
}

/**
 * Show the guest limit reached modal
 */
function showLimitReached() {
  // Get the limit reached modal
  const limitReachedModal = document.getElementById("limitReachedModal");

  // If the modal is found, display it
  if (limitReachedModal) {
    limitReachedModal.style.display = "flex";

    // Update the modal message if needed based on user type
    const modalDescription =
      limitReachedModal.querySelector(".modal-description");
    if (modalDescription) {
      if (store.getIsGuestMode()) {
        modalDescription.textContent =
          "You've reached your guest mode token limit for today. To continue using Quantum, please connect your Solana wallet.";
      } else {
        modalDescription.textContent =
          "You've reached your daily token allowance. To increase your daily limit, you need to acquire more LANE tokens. Each 1,000 LANE tokens increases your daily allowance.";
      }
    }
  }
}

/**
 * Hide the guest limit reached modal
 */
function hideLimitReached() {
  limitReachedModal.style.display = "none";
}

/**
 * Show the logout confirmation modal
 */
function showLogoutConfirmation() {
  logoutModal.style.display = "flex";
}

/**
 * Hide the logout confirmation modal
 */
function hideLogoutConfirmation() {
  logoutModal.style.display = "none";
}

/**
 * Hide the wallet modal
 */
function hideWalletModal() {
  const walletModal = document.getElementById("walletModal");
  const loginModal = document.getElementById("loginModal");
  const isAuthenticated = localStorage.getItem("alaricAuthToken") !== null;

  if (walletModal) {
    walletModal.style.display = "none";

    if (!isAuthenticated && loginModal) {
      loginModal.style.display = "flex";
    }
  }
}

/**
 * Apply mobile-specific styles and behavior
 */
function setupMobileUI() {
  if (isMobileDevice()) {
    addMobileStyles();
    setupMobileSidebarClickOutside();
  }
}

/**
 * Check if the device is mobile
 * @returns {boolean} - True if the device is mobile
 */
function isMobileDevice() {
  return (
    window.innerWidth <= 768 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  );
}

/**
 * Add mobile-specific styles
 */
function addMobileStyles() {
  const styleElement = document.createElement("style");
  styleElement.textContent = `
    /* Mobile sidebar styles */
    .sidebar {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 100;
      background-color: transparent;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      width: 80%;
      max-width: 300px;
      height: 100%;
      transform: translateX(-100%);
      transition: transform 0.3s ease;
      border-right: 1px solid var(--border);
    }
    
    .sidebar.active {
      transform: translateX(0);
      box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    }
    
    /* Mobile menu button */
    .menu-btn {
      position: fixed;
      z-index: 95;
      background-color: rgba(0, 0, 0, 0.9);
      color: white;
      border: none;
      border-right: 1px solid var(--accent);
      border-bottom: 1px solid var(--accent);
      border-radius: 0 8px 8px 0;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
      cursor: pointer;
      transition: transform 0.2s ease;
    }
    
    .menu-btn:hover {
      transform: scale(1.1);
    }
    
    /* Mobile main content */
    .main-content {
      padding-top: 60px;
      width: 100%;
      height: calc(100% - 60px);
    }
    
    /* Mobile message input area */
    .input-container {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      padding: 10px;
      background-color: var(--bg-primary);
      z-index: 90;
      border-top: 1px solid var(--border);
    }
    
    /* Mobile messages container */
    .messages-container {
      height: calc(100% - 120px);
      padding-bottom: 70px;
      width: 100%;
    }
    
    /* Mobile modal styles */
    .modal .modal-content {
      width: 90%;
      max-width: 350px;
    }
    
    /* Action button style */
    .action-btn {
      background-color: var(--accent);
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: bold;
      transition: background-color 0.2s;
    }
    
    .action-btn:hover {
      background-color: var(--accent-hover);
    }
    
    /* Fix for chat container on mobile */
    .chat-container {
      width: 100%;
    }
  `;
  document.head.appendChild(styleElement);

  if (!document.querySelector(".menu-btn")) {
    const menuBtn = document.createElement("button");
    menuBtn.className = "menu-btn";
    menuBtn.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="3" y1="12" x2="21" y2="12"></line>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <line x1="3" y1="18" x2="21" y2="18"></line>
      </svg>
    `;

    menuBtn.addEventListener("click", toggleMobileSidebar);
    document.body.appendChild(menuBtn);
  }
}

/**
 * Setup click-outside behavior for mobile sidebar
 */
function setupMobileSidebarClickOutside() {
  document.addEventListener("click", function (event) {
    const sidebar = document.querySelector(".sidebar");
    const menuBtn = document.querySelector(".menu-btn");

    if (
      sidebar &&
      sidebar.classList.contains("active") &&
      !sidebar.contains(event.target) &&
      menuBtn &&
      !menuBtn.contains(event.target)
    ) {
      sidebar.classList.remove("active");
    }
  });
}

/**
 * Toggle mobile sidebar visibility
 */
function toggleMobileSidebar() {
  const sidebar = document.querySelector(".sidebar");
  if (sidebar) {
    sidebar.classList.toggle("active");
  }
}

// Fonction corrigée pour showDisclaimerModal dans ui.js
function showDisclaimerModal(callback) {
  const disclaimerModal = document.getElementById("disclaimerModal");
  if (disclaimerModal) {
    disclaimerModal.style.display = "flex";

    const checkbox = document.getElementById("disclaimerCheckbox");
    const agreeBtn = document.getElementById("disclaimerAgreeBtn");

    // Réinitialiser les écouteurs précédents
    const newAgreeBtn = agreeBtn.cloneNode(true);
    agreeBtn.parentNode.replaceChild(newAgreeBtn, agreeBtn);

    if (checkbox && newAgreeBtn) {
      // Réinitialiser la checkbox
      checkbox.checked = false;
      newAgreeBtn.disabled = true;

      checkbox.addEventListener("change", function () {
        newAgreeBtn.disabled = !this.checked;
      });

      newAgreeBtn.addEventListener("click", function () {
        if (checkbox.checked) {
          localStorage.setItem("quantumDisclaimerAccepted", "true");
          disclaimerModal.style.display = "none";
          const appContainer = document.getElementById("appContainer");
          if (appContainer) appContainer.style.display = "flex";
          if (callback && typeof callback === "function") {
            callback();
          }
        }
      });
    }
  }
}

// Fonction corrigée pour vérifier si le disclaimer a été accepté
function hasAcceptedDisclaimer() {
  return localStorage.getItem("quantumDisclaimerAccepted") === "true";
}

async function generateQrCode() {
  try {
    const currentWalletAddress = store.getWalletAddress();

    if (!store.getIsAuthenticated() || !currentWalletAddress) {
      showNotification("Please connect your wallet first", "warning");
      return;
    }

    const authToken = localStorage.getItem("quantumAuthToken");
    if (!authToken) {
      throw new Error("Not authenticated");
    }

    const qrCodeModal = document.getElementById("qrCodeModal");
    if (qrCodeModal) {
      qrCodeModal.style.display = "flex";
    }

    const qrCodeContainer = document.getElementById("qrCodeContainer");
    qrCodeContainer.innerHTML =
      '<div style="padding: 20px;">Generating QR code...</div>';

    const response = await fetch(`${apiUrl}/generate-mobile-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        walletAddress: currentWalletAddress,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to generate mobile token");
    }

    const data = await response.json();

    // Create a URL with the format that's more likely to be recognized
    const baseUrl = "https://alaric.ppline.app";
    const params = `loginToken=${data.token}&walletAddress=${encodeURIComponent(currentWalletAddress)}&ts=${Date.now()}`;
    const qrUrl = `${baseUrl}?${params}`;
    // Log the URL for debugging
    console.log("QR Code URL:", qrUrl);

    // Load QRCode library if not already loaded
    if (!window.QRCode) {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/qrcode@1.5.1/build/qrcode.min.js";
      document.head.appendChild(script);
      await new Promise((resolve) => (script.onload = resolve));
    }

    qrCodeContainer.innerHTML = "";

    // Create an image element instead of canvas for better compatibility
    const qrImage = document.createElement("img");
    qrImage.style.width = "256px";
    qrImage.style.height = "256px";
    qrCodeContainer.appendChild(qrImage);

    // Use toDataURL instead of toCanvas for better compatibility
    QRCode.toDataURL(
      qrUrl,
      {
        width: 256,
        margin: 1,
        color: {
          dark:
            "#000000",
          light:
            "#FFFFFF",
        },
        errorCorrectionLevel: "M", // High error correction for better scanning
      },
      (err, url) => {
        if (err) throw err;
        qrImage.src = url;

        // Add text links for backup access
        const linkText = document.createElement("p");
        linkText.style.marginTop = "10px";
        linkText.style.fontSize = "0.8rem";
        linkText.innerHTML =
          "Can't scan? <a href='" +
          qrUrl +
          "' target='_blank'>Open link directly</a>";
        qrCodeContainer.appendChild(linkText);

        const expirationText = document.createElement("p");
        expirationText.style.marginTop = "5px";
        expirationText.style.fontSize = "0.8rem";
        expirationText.style.color = "var(--text-secondary)";
        expirationText.textContent = "This QR code will expire in 5 minutes";
        qrCodeContainer.appendChild(expirationText);
      }
    );
  } catch (error) {
    console.error("Error generating QR code:", error);
    showNotification(`Error: ${error.message}`, "error");

    const qrCodeModal = document.getElementById("qrCodeModal");
    if (qrCodeModal) {
      qrCodeModal.style.display = "none";
    }
  }
}

function closeQrCodeModal() {
  const qrCodeModal = document.getElementById("qrCodeModal");
  if (qrCodeModal) {
    qrCodeModal.style.display = "none";
  }
}

function addQrCodeButton() {
  if (store.getIsAuthenticated() && !isMobileDevice()) {
    const creditsInfo = document.getElementById("creditsInfo");
    if (creditsInfo) {
      if (!document.getElementById("generateQrBtn")) {
        const qrButton = document.createElement("button");
        qrButton.id = "generateQrBtn";
        qrButton.className = "action-btn";
        qrButton.title = "Generate QR Code for Mobile";
        qrButton.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="8" y1="3" x2="8" y2="8"></line>
            <line x1="3" y1="8" x2="8" y2="8"></line>
            <line x1="16" y1="3" x2="16" y2="8"></line>
            <line x1="16" y1="8" x2="21" y2="8"></line>
            <line x1="3" y1="16" x2="8" y2="16"></line>
            <line x1="8" y1="16" x2="8" y2="21"></line>
            <line x1="16" y1="16" x2="21" y2="16"></line>
            <line x1="16" y1="16" x2="16" y2="21"></line>
          </svg>
          Mobile QR Code
        `;
        qrButton.style.width = "100%";
        qrButton.style.marginBottom = "10px";
        qrButton.style.padding = "8px 12px";
        qrButton.style.display = "flex";
        qrButton.style.alignItems = "center";
        qrButton.style.justifyContent = "center";
        qrButton.addEventListener("click", generateQrCode);

        const creditsContainer = creditsInfo.parentNode;
        creditsContainer.insertBefore(qrButton, creditsInfo);

        const addCreditsBtn = document.getElementById("addCreditsBtn");
        if (addCreditsBtn) {
          addCreditsBtn.className = "action-btn";
          addCreditsBtn.style.padding = "5px";
          addCreditsBtn.style.display = "flex";
          addCreditsBtn.style.marginLeft = "10px";
          addCreditsBtn.style.alignItems = "center";
          addCreditsBtn.style.justifyContent = "center";

          const originalText = addCreditsBtn.textContent;
          addCreditsBtn.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            ${originalText}
          `;
        }
      }
    }
  }
}

export {
  setupUI,
  showConfirm,
  showLearnMore,
  hideLearnMore,
  showLimitReached,
  hideLimitReached,
  showLogoutConfirmation,
  hideLogoutConfirmation,
  isMobileDevice,
  setupMobileUI,
  showDisclaimerModal,
  hasAcceptedDisclaimer,
  hideWalletModal,
  generateQrCode,
  closeQrCodeModal,
  addQrCodeButton,
};
